import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import ChangeReview from "@/components/ChangeReview";
import { X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const LIFE = {
  live: "Live", inspection_pending: "Inspection Pending", inspection_passed: "Inspection Passed",
  admin_review: "Admin Review", draft: "Draft", submitted: "Submitted",
  suspended: "Suspended", archived: "Archived", rejected: "Rejected",
};
const PENDING = ["draft", "submitted", "inspection_pending", "inspection_passed", "admin_review"];
const TABS = ["Overview", "Media", "Variants", "Pricing", "Inspection", "Compliance", "Change Requests", "Activity"];

const Row = ({ k, v }) => (
  <div className="grid grid-cols-[160px_1fr] gap-4 py-2 border-b border-foreground/10">
    <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{k}</p>
    <div className="text-sm">{v == null || v === "" ? <span className="text-muted-foreground italic">—</span> : v}</div>
  </div>
);
const Section = ({ title, children }) => (
  <div className="mb-8">
    {title && <p className="font-mono text-[10px] uppercase tracking-widest text-[hsl(var(--accent))] mb-3">{title}</p>}
    {children}
  </div>
);

export default function AdminProductDetail({ productId, onClose, onChanged, initialTab }) {
  const { user } = useAuth();
  const can = (perm) => user?.admin_roles?.includes("super_admin") || (user?.permissions || []).includes(perm);
  const [p, setP] = useState(null);
  const [crs, setCrs] = useState([]);
  const [activity, setActivity] = useState([]);
  const [tab, setTab] = useState(initialTab || "Overview");
  const [busy, setBusy] = useState(false);
  const [reasonFor, setReasonFor] = useState(null);
  const [reason, setReason] = useState("");
  const [reviewCR, setReviewCR] = useState(null);
  const [insp, setInsp] = useState({ authenticity_pass: true, quality_pass: true, size_fit_pass: true, skeptic_notes: "", perfectionist_notes: "", softie_notes: "" });

  const load = async () => {
    const [pd, c, a] = await Promise.all([
      api.get(`/products/${productId}`).then((r) => r.data),
      api.get(`/admin/products/${productId}/change-requests`).then((r) => r.data).catch(() => []),
      api.get(`/admin/products/${productId}/activity`).then((r) => r.data).catch(() => []),
    ]);
    setP(pd); setCrs(c); setActivity(a);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [productId]);

  const act = async (url, body) => {
    setBusy(true);
    try { await api.post(url, body || {}); toast.success("Done"); await load(); onChanged?.(); return true; }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Action failed"); return false; }
    finally { setBusy(false); }
  };
  const submitReason = async () => {
    if (!reason.trim()) return toast.error("A reason is required.");
    if (await act(`/admin/products/${productId}/${reasonFor}`, { reason })) { setReasonFor(null); setReason(""); }
  };
  const submitInspection = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/products/${productId}/certify`, insp);
      toast.success(data.passed ? "Inspection passed & certified" : "Inspection recorded as failed");
      await load(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  const Overlay = ({ children }) => (
    <div className="fixed inset-0 z-[65] bg-foreground/40 flex items-stretch md:items-center md:justify-center md:p-6" onClick={onClose}>
      <div className="bg-background border-2 border-foreground w-full md:max-w-4xl md:max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>{children}</div>
    </div>
  );

  if (!p) return <Overlay><p className="p-12 font-mono text-sm">Loading…</p></Overlay>;

  const s = p.lifecycle;
  const cert = p.spotted_certificate;
  const pendingCount = crs.filter((c) => c.status === "pending").length;
  const variants = p.variants || [];

  return (
    <Overlay>
      {/* Header */}
      <div className="px-6 py-4 border-b border-foreground flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{p.sku || "—"} · {p.vendor_name}</p>
          <h2 className="font-serif text-3xl tracking-tight truncate">{p.title}</h2>
          <div className="flex gap-2 mt-2 flex-wrap">
            <span className={`font-mono text-[9px] uppercase tracking-widest border px-1.5 py-0.5 ${s === "live" ? "text-[hsl(var(--accent))] border-[hsl(var(--accent))]" : (s === "suspended" || s === "rejected") ? "text-destructive border-destructive" : "text-muted-foreground border-foreground/30"}`}>{LIFE[s] || s}</span>
            {cert?.passed && <span className="font-mono text-[9px] uppercase tracking-widest border border-foreground/30 px-1.5 py-0.5">Spotter Certified</span>}
            {p.has_pending_change && <span className="font-mono text-[9px] uppercase tracking-widest border border-[hsl(var(--accent))] text-[hsl(var(--accent))] px-1.5 py-0.5">Change pending</span>}
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {can("product.approve") && (
            <div className="hidden sm:flex gap-1.5 flex-wrap justify-end max-w-[280px]">
              {(PENDING.includes(s) || s === "rejected") && <button disabled={busy} onClick={() => act(`/admin/products/${productId}/approve`)} className="bg-foreground text-background px-3 py-1.5 text-[10px] font-mono uppercase hover:bg-[hsl(var(--accent))] disabled:opacity-50">Approve</button>}
              {PENDING.includes(s) && <button onClick={() => setReasonFor("reject")} className="border border-destructive text-destructive px-3 py-1.5 text-[10px] font-mono uppercase">Reject</button>}
              {s === "live" && <button onClick={() => setReasonFor("suspend")} className="border border-destructive text-destructive px-3 py-1.5 text-[10px] font-mono uppercase">Suspend</button>}
              {(s === "suspended" || s === "archived") && <button disabled={busy} onClick={() => act(`/admin/products/${productId}/restore`)} className="bg-foreground text-background px-3 py-1.5 text-[10px] font-mono uppercase hover:bg-[hsl(var(--accent))]">Restore</button>}
              {s !== "archived" && <button onClick={() => setReasonFor("archive")} className="border border-foreground/40 px-3 py-1.5 text-[10px] font-mono uppercase">Archive</button>}
            </div>
          )}
          <button onClick={onClose} aria-label="close" className="hover:text-[hsl(var(--accent))]"><X size={20} strokeWidth={1.5} /></button>
        </div>
      </div>

      {/* Tabs */}
      <div className="px-6 flex gap-4 overflow-x-auto border-b border-foreground/20 font-mono text-[11px] uppercase tracking-widest">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} className={`py-3 border-b-2 whitespace-nowrap ${tab === t ? "border-[hsl(var(--accent))] text-[hsl(var(--accent))]" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            {t}{t === "Change Requests" && pendingCount > 0 && <span className="ml-1 text-[hsl(var(--accent))]">({pendingCount})</span>}
          </button>
        ))}
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-6">
        {tab === "Overview" && (
          <Section title="Product information">
            <Row k="Name" v={p.title} />
            <Row k="Brand" v={p.brand} />
            <Row k="Department" v={p.department} />
            <Row k="Category" v={[p.category_name || p.category, p.subcategory_name].filter(Boolean).join(" → ")} />
            <Row k="Description" v={<span className="whitespace-pre-wrap">{p.description}</span>} />
            <Row k="Material" v={p.material} />
            <Row k="Highlights" v={(p.highlights || []).join(", ")} />
            <Row k="Care" v={<span className="whitespace-pre-wrap">{p.care}</span>} />
            {Object.entries(p.attributes || {}).filter(([, v]) => v && (!Array.isArray(v) || v.length)).map(([k, v]) => (
              <Row key={k} k={k.replace(/_/g, " ")} v={Array.isArray(v) ? v.join(", ") : String(v)} />
            ))}
          </Section>
        )}

        {tab === "Media" && (
          <>
            <Section title="Images">
              <div className="grid grid-cols-4 gap-2">
                {(p.images || []).map((src, i) => <div key={i} className="aspect-square bg-secondary overflow-hidden">{src && <img src={src} alt="" className="w-full h-full object-cover" />}</div>)}
                {(p.images || []).length === 0 && <p className="text-sm text-muted-foreground">No images.</p>}
              </div>
            </Section>
            {Object.keys(p.colour_images || {}).length > 0 && (
              <Section title="Images by colour">
                {Object.entries(p.colour_images).map(([c, imgs]) => (
                  <div key={c} className="mb-3">
                    <p className="font-mono text-[10px] uppercase tracking-widest mb-1">{c}</p>
                    <div className="grid grid-cols-6 gap-2">{(imgs || []).map((src, i) => <div key={i} className="aspect-square bg-secondary overflow-hidden">{src && <img src={src} alt="" className="w-full h-full object-cover" />}</div>)}</div>
                  </div>
                ))}
              </Section>
            )}
            <Section title="Video">{p.video_url ? <a href={p.video_url} target="_blank" rel="noreferrer" className="text-sm underline break-all">{p.video_url}</a> : <p className="text-sm text-muted-foreground">None.</p>}</Section>
          </>
        )}

        {tab === "Variants" && (
          <Section title="Variants">
            {variants.length ? (
              <table className="w-full text-sm">
                <thead><tr className="border-b border-foreground font-mono text-[10px] uppercase tracking-widest"><th className="text-left py-2">Size</th><th className="text-left">Colour</th><th className="text-left">SKU</th><th className="text-right">Stock</th></tr></thead>
                <tbody>{variants.map((v, i) => <tr key={i} className="border-b border-foreground/15"><td className="py-2">{v.size}</td><td>{v.colour}</td><td className="font-mono text-xs">{v.sku}</td><td className="text-right font-mono">{v.stock}</td></tr>)}</tbody>
                <tfoot><tr className="font-mono text-xs"><td colSpan={3} className="pt-2 text-right text-muted-foreground uppercase tracking-widest">Total stock</td><td className="pt-2 text-right">{p.stock}</td></tr></tfoot>
              </table>
            ) : <div><Row k="Sizes" v={(p.sizes || []).join(", ")} /><Row k="Colours" v={(p.colors || []).join(", ")} /><Row k="Total stock" v={p.stock} /></div>}
          </Section>
        )}

        {tab === "Pricing" && (
          <Section title="Commercial">
            <Row k="MRP" v={p.mrp ? `₹${Number(p.mrp).toLocaleString("en-IN")}` : null} />
            <Row k="Selling price" v={`₹${Number(p.price || 0).toLocaleString("en-IN")}`} />
            <Row k="GST rate" v={p.gst_rate != null ? `${p.gst_rate}%` : null} />
            <Row k="HSN code" v={p.hsn_code} />
            <p className="mt-4 font-mono text-[10px] text-muted-foreground">Price editing will be gated to authorized roles when RBAC is added.</p>
          </Section>
        )}

        {tab === "Inspection" && (
          <>
            <Section title="Physical inspection">
              {cert ? (
                <>
                  <Row k="Result" v={<span className={cert.passed ? "text-[hsl(var(--accent))]" : "text-destructive"}>{cert.passed ? "PASSED" : "FAILED"}</span>} />
                  <Row k="The Skeptic (authenticity)" v={cert.authenticity_pass ? "Pass" : "Fail"} />
                  <Row k="The Perfectionist (quality)" v={cert.quality_pass ? "Pass" : "Fail"} />
                  <Row k="The Softie (fit)" v={cert.size_fit_pass ? "Pass" : "Fail"} />
                  {cert.skeptic_notes && <Row k="Skeptic notes" v={cert.skeptic_notes} />}
                  {cert.perfectionist_notes && <Row k="Perfectionist notes" v={cert.perfectionist_notes} />}
                  {cert.softie_notes && <Row k="Softie notes" v={cert.softie_notes} />}
                  {cert.certified_at && <Row k="Inspected at" v={new Date(cert.certified_at).toLocaleString("en-IN")} />}
                </>
              ) : <p className="text-sm text-muted-foreground">Not yet inspected.</p>}
            </Section>
            {can("product.inspect") && <Section title={cert ? "Re-inspect" : "Record inspection"}>
              {[["authenticity_pass", "The Skeptic — Authenticity", "skeptic_notes"], ["quality_pass", "The Perfectionist — Quality", "perfectionist_notes"], ["size_fit_pass", "The Softie — Fit & Comfort", "softie_notes"]].map(([passKey, label, noteKey]) => (
                <div key={passKey} className="mb-4 border border-foreground/20 p-3">
                  <div className="flex items-center justify-between">
                    <p className="font-mono text-[11px] uppercase tracking-widest">{label}</p>
                    <div className="flex gap-1">
                      <button onClick={() => setInsp((x) => ({ ...x, [passKey]: true }))} className={`px-2 py-1 text-[10px] font-mono uppercase ${insp[passKey] ? "bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]" : "border border-foreground/30"}`}>Pass</button>
                      <button onClick={() => setInsp((x) => ({ ...x, [passKey]: false }))} className={`px-2 py-1 text-[10px] font-mono uppercase ${!insp[passKey] ? "bg-destructive text-destructive-foreground" : "border border-foreground/30"}`}>Fail</button>
                    </div>
                  </div>
                  <input value={insp[noteKey]} onChange={(e) => setInsp((x) => ({ ...x, [noteKey]: e.target.value }))} placeholder="Notes (optional)" className="w-full mt-2 border-b border-foreground/30 py-1 text-sm outline-none focus:border-[hsl(var(--accent))]" />
                </div>
              ))}
              <button disabled={busy} onClick={submitInspection} className="bg-foreground text-background px-6 py-3 font-mono text-xs uppercase tracking-widest hover:bg-[hsl(var(--accent))] disabled:opacity-50">Submit inspection</button>
              <p className="mt-2 font-mono text-[10px] text-muted-foreground">All three must pass to certify. Passing also approves the product.</p>
            </Section>}
          </>
        )}

        {tab === "Compliance" && (
          <Section title="Compliance & sourcing">
            <Row k="Country of origin" v={p.country_of_origin} />
            <Row k="Manufacturer" v={p.manufacturer} />
            <Row k="Manufacturer address" v={p.manufacturer_address} />
            <Row k="HSN code" v={p.hsn_code} />
            <Row k="GST rate" v={p.gst_rate != null ? `${p.gst_rate}%` : null} />
            <p className="mt-4 font-mono text-[10px] text-muted-foreground">BIS certification tracking can be added as a dedicated compliance field.</p>
          </Section>
        )}

        {tab === "Change Requests" && (
          crs.length === 0 ? <p className="font-serif text-xl text-muted-foreground">No change requests for this product.</p> : (
            <div className="space-y-3">
              {crs.map((c) => (
                <div key={c.id} className="border border-foreground/25 p-4 flex items-center justify-between gap-4">
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{new Date(c.created_at).toLocaleString("en-IN")} · by {c.submitted_by_name || c.vendor_name}</p>
                    <p className="text-sm mt-1">Changed: {(c.changes || []).map((ch) => ch.field.replace(/_/g, " ")).join(", ")}</p>
                    <span className={`inline-block mt-1 font-mono text-[9px] uppercase tracking-widest border px-1.5 py-0.5 ${c.status === "pending" ? "text-[hsl(var(--accent))] border-[hsl(var(--accent))]" : c.status === "rejected" ? "text-destructive border-destructive" : "text-muted-foreground border-foreground/30"}`}>{c.status}</span>
                  </div>
                  {c.status === "pending" && can("product.approve") && <button onClick={() => setReviewCR(c)} className="bg-foreground text-background px-4 py-2 text-xs font-mono uppercase hover:bg-[hsl(var(--accent))] shrink-0">Review →</button>}
                </div>
              ))}
            </div>
          )
        )}

        {tab === "Activity" && (
          activity.length === 0 ? <p className="font-serif text-xl text-muted-foreground">No recorded activity yet.</p> : (
            <div className="border-l border-foreground/20 pl-4 space-y-4">
              {activity.map((a) => (
                <div key={a.id}>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{new Date(a.at).toLocaleString("en-IN")} · {a.actor_name}</p>
                  <p className="text-sm mt-0.5">{a.action.replace(/\./g, " · ").replace(/_/g, " ")}{a.reason ? ` — “${a.reason}”` : ""}</p>
                </div>
              ))}
            </div>
          )
        )}
      </div>

      {reviewCR && <ChangeReview cr={reviewCR} onClose={() => setReviewCR(null)} onDone={() => { setReviewCR(null); load(); onChanged?.(); }} />}

      {reasonFor && (
        <div className="fixed inset-0 z-[75] bg-foreground/40 flex items-center justify-center p-4" onClick={() => setReasonFor(null)}>
          <div className="bg-background border-2 border-foreground w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Reason required</p>
            <h3 className="font-serif text-2xl tracking-tight mt-1 capitalize">{reasonFor} — {p.title}</h3>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} autoFocus placeholder="Why? (recorded in the audit log)" className="w-full mt-4 border border-foreground p-3 text-sm min-h-[90px] outline-none focus:border-[hsl(var(--accent))]" />
            <div className="flex gap-3 mt-4 justify-end">
              <button onClick={() => setReasonFor(null)} className="border border-foreground px-5 py-2.5 font-mono text-[11px] uppercase tracking-widest hover:bg-foreground hover:text-background">Cancel</button>
              <button disabled={busy || !reason.trim()} onClick={submitReason} className="bg-destructive text-destructive-foreground px-5 py-2.5 font-mono text-[11px] uppercase tracking-widest disabled:opacity-50">Confirm</button>
            </div>
          </div>
        </div>
      )}
    </Overlay>
  );
}
